import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Paper, Typography, Table, TableHead, TableRow, TableCell, TableBody, Chip, Stack,
  Button, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Alert, Grid,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import PublishedWithChangesRoundedIcon from '@mui/icons-material/PublishedWithChangesRounded';
import BlockRoundedIcon from '@mui/icons-material/BlockRounded';
import { addRule, publishRule, disableRule, testRule, addSuppression, runHunt, clearTestResult, simulateEvent } from './detectionEngineeringSlice';
import { tokens } from '../../theme/theme';

const EVENT_TYPES = ['LOGIN_ATTEMPT', 'HTTP_REQUEST', 'FILE_UPLOAD'];
const SEVERITIES = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];
const CONDITION_FIELDS = ['source_ip', 'username', 'target', 'method', 'query_string', 'filename', 'sha256'];
const CONDITION_OPERATORS = ['equals', 'contains', 'regex', 'greater_than'];
const SUPPRESSION_FIELDS = ['source_ip', 'username', 'target'];
const STATUS_COLOR = { DRAFT: tokens.severity.medium, PUBLISHED: tokens.severity.low, DISABLED: tokens.severity.critical };

const emptySuppression = { rule_id: '', field: 'source_ip', value: '', reason: '', expires_at: '' };
const toLocalInput = (d) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
const defaultHunt = () => ({ event_type: '', source_ip: '', username: '', target_contains: '', since: toLocalInput(new Date(Date.now() - 24 * 3600 * 1000)), until: toLocalInput(new Date(Date.now() + 5 * 60000)), limit: 100 });
const emptySimulation = { event_type: 'LOGIN_ATTEMPT', source_ip: '203.0.113.5', username: '', target: '', payload: '{}' };


let conditionSeq = 0;
const newCondition = () => ({ key: `c${conditionSeq += 1}`, field: 'source_ip', operator: 'equals', value: '' });
const newRuleDraft = () => ({ name: '', description: '', event_type: 'LOGIN_ATTEMPT', severity: 'MEDIUM', attack_ids: '', conditions: [newCondition()] });

// Self-contained dialog: it is mounted only while open, owns its own form state (so each
// open starts from a fresh draft and can never render a second copy of the fields), and
// keys condition rows by a stable id rather than by array index.
function NewRuleDialog({ onClose, onSubmit }) {
  const [form, setForm] = useState(newRuleDraft);
  const [formError, setFormError] = useState(null);
  const [saving, setSaving] = useState(false);

  const set = (field, value) => setForm((f) => ({ ...f, [field]: value }));
  const updateCondition = (key, field, value) => setForm((f) => ({
    ...f, conditions: f.conditions.map((c) => (c.key === key ? { ...c, [field]: value } : c)),
  }));
  const addCondition = () => setForm((f) => ({ ...f, conditions: [...f.conditions, newCondition()] }));
  const removeCondition = (key) => setForm((f) => ({ ...f, conditions: f.conditions.length === 1 ? f.conditions : f.conditions.filter((c) => c.key !== key) }));

  const submit = async () => {
    if (!form.name.trim()) return setFormError('Rule name is required.');
    if (form.conditions.some((c) => !c.value.trim())) return setFormError('Every condition needs a value.');
    setFormError(null);
    setSaving(true);
    try {
      await onSubmit({
        name: form.name.trim(),
        description: form.description.trim() || null,
        event_type: form.event_type,
        severity: form.severity,
        attack_ids: form.attack_ids.split(',').map((x) => x.trim()).filter(Boolean),
        conditions: form.conditions.map(({ field, operator, value }) => ({ field, operator, value: value.trim() })),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>New Detection Rule</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {formError && <Alert severity="error">{formError}</Alert>}
          <TextField label="Name" size="small" value={form.name} onChange={(e) => set('name', e.target.value)} autoFocus />
          <TextField label="Description" size="small" multiline minRows={2} value={form.description} onChange={(e) => set('description', e.target.value)} />
          <Stack direction="row" spacing={2}>
            <TextField select label="Event Type" size="small" value={form.event_type} onChange={(e) => set('event_type', e.target.value)} sx={{ flex: 1 }}>
              {EVENT_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </TextField>
            <TextField select label="Severity" size="small" value={form.severity} onChange={(e) => set('severity', e.target.value)} sx={{ flex: 1 }}>
              {SEVERITIES.map((sv) => <MenuItem key={sv} value={sv}>{sv}</MenuItem>)}
            </TextField>
          </Stack>
          <TextField label="ATT&CK IDs (comma-separated)" size="small" placeholder="T1110, T1078" value={form.attack_ids} onChange={(e) => set('attack_ids', e.target.value)} />
          <Typography variant="caption" sx={{ color: tokens.text.muted }}>Conditions (all must match)</Typography>
          {form.conditions.map((c) => (
            <Stack direction="row" spacing={1} key={c.key} alignItems="center">
              <TextField select size="small" value={c.field} onChange={(e) => updateCondition(c.key, 'field', e.target.value)} sx={{ flex: 1 }}>
                {CONDITION_FIELDS.map((f) => <MenuItem key={f} value={f}>{f}</MenuItem>)}
              </TextField>
              <TextField select size="small" value={c.operator} onChange={(e) => updateCondition(c.key, 'operator', e.target.value)} sx={{ flex: 1 }}>
                {CONDITION_OPERATORS.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
              </TextField>
              <TextField size="small" placeholder="Value" value={c.value} onChange={(e) => updateCondition(c.key, 'value', e.target.value)} sx={{ flex: 1.4 }} />
              <IconButton size="small" onClick={() => removeCondition(c.key)} disabled={form.conditions.length === 1}><DeleteOutlineRoundedIcon fontSize="small" /></IconButton>
            </Stack>
          ))}
          <Button size="small" startIcon={<AddRoundedIcon />} onClick={addCondition} sx={{ alignSelf: 'flex-start' }}>Add Condition</Button>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={saving}>Create Rule (Draft)</Button>
      </DialogActions>
    </Dialog>
  );
}

export default function DetectionEngineeringPanel() {
  const dispatch = useDispatch();
  const { rules, suppressions, huntResults, huntRanAt, lastTestResult, lastSimulation } = useSelector((s) => s.detectionEngineering);

  const [ruleDialogOpen, setRuleDialogOpen] = useState(false);
  const [suppressionDialogOpen, setSuppressionDialogOpen] = useState(false);
  const [suppressionForm, setSuppressionForm] = useState(emptySuppression);
  const [testTarget, setTestTarget] = useState(null);
  const [testEventJson, setTestEventJson] = useState('{\n  "source_ip": "203.0.113.5"\n}');
  const [huntForm, setHuntForm] = useState(defaultHunt);
  const [error, setError] = useState(null);
  const [hunting, setHunting] = useState(false);
  const [simForm, setSimForm] = useState(emptySimulation);
  const [simulating, setSimulating] = useState(false);

  const runAction = async (thunk, arg) => {
    setError(null);
    const result = await dispatch(thunk(arg));
    if (thunk.rejected.match(result)) {
      setError(result.error?.message || 'That action could not be completed.');
      return false;
    }
    return true;
  };

  const handleTestRule = async () => {
    let parsed;
    try { parsed = JSON.parse(testEventJson); } catch { return setError('Test event must be valid JSON.'); }
    await runAction(testRule, { id: testTarget.id, payload: { event: parsed } });
  };

  const handleCreateSuppression = async () => {
    if (!suppressionForm.value.trim() || !suppressionForm.reason.trim() || !suppressionForm.expires_at) {
      return setError('Value, reason, and expiry are all required.');
    }
    const ok = await runAction(addSuppression, {
      ...suppressionForm,
      rule_id: suppressionForm.rule_id || null,
      expires_at: new Date(suppressionForm.expires_at).toISOString(),
    });
    if (ok) { setSuppressionDialogOpen(false); setSuppressionForm(emptySuppression); }
  };

  const handleRunHunt = async () => {
    if (!huntForm.since || !huntForm.until) return setError('Both a start and end time are required for a hunt window.');
    setHunting(true);
    await runAction(runHunt, {
      ...huntForm,
      event_type: huntForm.event_type || null,
      source_ip: huntForm.source_ip || null,
      username: huntForm.username || null,
      target_contains: huntForm.target_contains || null,
      since: new Date(huntForm.since).toISOString(),
      until: new Date(huntForm.until).toISOString(),
    });
    setHunting(false);
  };

  const handleSimulate = async () => {
    let extra;
    try { extra = simForm.payload.trim() ? JSON.parse(simForm.payload) : {}; } catch { return setError('Additional fields must be valid JSON.'); }
    setSimulating(true);
    await runAction(simulateEvent, {
      event_type: simForm.event_type,
      source_ip: simForm.source_ip.trim() || null,
      username: simForm.username.trim() || null,
      target: simForm.target.trim() || null,
      payload: extra,
    });
    setSimulating(false);
  };

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Paper sx={{ overflow: 'hidden', mb: 2.5 }}>
        <Box sx={{ p: 2, pb: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="overline" sx={{ color: tokens.text.muted, fontSize: '0.65rem' }}>Detection Rules</Typography>
          <Button size="small" startIcon={<AddRoundedIcon />} onClick={() => { setError(null); setRuleDialogOpen(true); }}>New Rule</Button>
        </Box>
        <Table size="small">
          <TableHead>
            <TableRow>
              {['Name', 'Event Type', 'Severity', 'Status', 'v', ''].map((h) => (
                <TableCell key={h} sx={{ color: tokens.text.muted, fontSize: '0.66rem', textTransform: 'uppercase' }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rules.map((r) => (
              <TableRow key={r.id} hover>
                <TableCell sx={{ fontSize: '0.78rem', color: tokens.text.primary, maxWidth: 220 }}>{r.name}</TableCell>
                <TableCell sx={{ fontSize: '0.72rem', color: tokens.text.secondary }}>{r.event_type}</TableCell>
                <TableCell sx={{ fontSize: '0.72rem', color: tokens.text.secondary }}>{r.severity}</TableCell>
                <TableCell><Chip label={r.status} size="small" sx={{ color: STATUS_COLOR[r.status], border: `1px solid ${STATUS_COLOR[r.status]}`, backgroundColor: 'transparent' }} /></TableCell>
                <TableCell sx={{ fontSize: '0.72rem', color: tokens.text.muted }}>{r.version}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <Tooltip title="Test against a sample event">
                    <IconButton size="small" onClick={() => setTestTarget(r)}><PlayArrowRoundedIcon fontSize="small" /></IconButton>
                  </Tooltip>
                  {r.status !== 'PUBLISHED' && (
                    <Tooltip title="Publish"><IconButton size="small" onClick={() => runAction(publishRule, r.id)}><PublishedWithChangesRoundedIcon fontSize="small" /></IconButton></Tooltip>
                  )}
                  {r.status === 'PUBLISHED' && (
                    <Tooltip title="Disable"><IconButton size="small" onClick={() => runAction(disableRule, r.id)}><BlockRoundedIcon fontSize="small" /></IconButton></Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {!rules.length && <TableRow><TableCell colSpan={6} sx={{ textAlign: 'center', color: tokens.text.muted, fontSize: '0.78rem', py: 3 }}>No detection rules yet — new rules start as DRAFT and must be published to take effect.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Paper>

      <Grid container spacing={2.5}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2.4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="overline" sx={{ color: tokens.text.muted, fontSize: '0.65rem' }}>Suppressions</Typography>
              <Button size="small" startIcon={<AddRoundedIcon />} onClick={() => setSuppressionDialogOpen(true)}>Add</Button>
            </Box>
            <Stack spacing={1} sx={{ mt: 1.5 }}>
              {suppressions.map((s) => (
                <Box key={s.id} sx={{ p: 1.2, borderRadius: 1.5, border: `1px solid ${tokens.border}` }}>
                  <Typography sx={{ fontSize: '0.78rem', color: tokens.text.primary }}>{s.field}: {s.value}</Typography>
                  <Typography sx={{ fontSize: '0.68rem', color: tokens.text.muted }}>{s.reason} · expires {new Date(s.expires_at).toLocaleDateString()}</Typography>
                </Box>
              ))}
              {!suppressions.length && <Typography sx={{ fontSize: '0.78rem', color: tokens.text.muted, textAlign: 'center', py: 2 }}>No active suppressions.</Typography>}
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2.4 }}>
            <Typography variant="overline" sx={{ color: tokens.text.muted, fontSize: '0.65rem' }}>Threat Hunting</Typography>
            <Stack spacing={1.2} sx={{ mt: 1.5 }}>
              <Stack direction="row" spacing={1}>
                <TextField select size="small" label="Event Type" value={huntForm.event_type} onChange={(e) => setHuntForm((f) => ({ ...f, event_type: e.target.value }))} sx={{ flex: 1 }}>
                  <MenuItem value="">Any</MenuItem>
                  {EVENT_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                </TextField>
                <TextField size="small" label="Source IP" value={huntForm.source_ip} onChange={(e) => setHuntForm((f) => ({ ...f, source_ip: e.target.value }))} sx={{ flex: 1 }} />
              </Stack>
              <Stack direction="row" spacing={1}>
                <TextField size="small" label="Username" value={huntForm.username} onChange={(e) => setHuntForm((f) => ({ ...f, username: e.target.value }))} sx={{ flex: 1 }} />
                <TextField size="small" label="Target contains" value={huntForm.target_contains} onChange={(e) => setHuntForm((f) => ({ ...f, target_contains: e.target.value }))} sx={{ flex: 1 }} />
              </Stack>
              <Stack direction="row" spacing={1}>
                <TextField size="small" type="datetime-local" label="Since" InputLabelProps={{ shrink: true }} value={huntForm.since} onChange={(e) => setHuntForm((f) => ({ ...f, since: e.target.value }))} sx={{ flex: 1 }} />
                <TextField size="small" type="datetime-local" label="Until" InputLabelProps={{ shrink: true }} value={huntForm.until} onChange={(e) => setHuntForm((f) => ({ ...f, until: e.target.value }))} sx={{ flex: 1 }} />
              </Stack>
              <Button variant="contained" size="small" onClick={handleRunHunt} disabled={hunting}>{hunting ? 'Searching…' : 'Run Hunt'}</Button>
              {huntResults.length > 0 && (
                <Box sx={{ maxHeight: 220, overflow: 'auto' }}>
                  {huntResults.map((r) => (
                    <Box key={r.id} sx={{ py: 0.6, borderBottom: `1px solid ${tokens.border}` }}>
                      <Typography sx={{ fontSize: '0.72rem', color: tokens.text.primary }}>{r.event_type} · {r.source_ip || '—'} · {r.username || '—'}</Typography>
                      <Typography sx={{ fontSize: '0.66rem', color: tokens.text.muted }}>{r.target || '—'} · {new Date(r.occurred_at).toLocaleString()}</Typography>
                    </Box>
                  ))}
                </Box>
              )}
              {huntRanAt && <Typography sx={{ fontSize: '0.7rem', color: tokens.text.secondary }}>{huntResults.length} event(s) found for this window.</Typography>}
              {huntRanAt && huntResults.length === 0 && (
                <Alert severity="info" sx={{ fontSize: '0.72rem' }}>
                  No security events match these filters in the selected window. If no events have been ingested yet, use Simulate Event below to create one.
                </Alert>
              )}
              {!huntRanAt && <Typography sx={{ fontSize: '0.7rem', color: tokens.text.muted }}>Run a hunt to search raw security events over a time window.</Typography>}
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      <Paper sx={{ p: 2.4, mt: 2.5 }}>
        <Typography variant="overline" sx={{ color: tokens.text.muted, fontSize: '0.65rem' }}>Simulate Event</Typography>
        <Typography sx={{ fontSize: '0.7rem', color: tokens.text.muted, mb: 1.5 }}>
          Records a labelled test event and runs it through every PUBLISHED rule, so alerts, Threat Hunting and incident conversion can be exercised end to end.
        </Typography>
        <Stack spacing={1.2}>
          <Stack direction="row" spacing={1}>
            <TextField select size="small" label="Event Type" value={simForm.event_type} onChange={(e) => setSimForm((f) => ({ ...f, event_type: e.target.value }))} sx={{ flex: 1 }}>
              {EVENT_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </TextField>
            <TextField size="small" label="Source IP" value={simForm.source_ip} onChange={(e) => setSimForm((f) => ({ ...f, source_ip: e.target.value }))} sx={{ flex: 1 }} />
            <TextField size="small" label="Username" value={simForm.username} onChange={(e) => setSimForm((f) => ({ ...f, username: e.target.value }))} sx={{ flex: 1 }} />
            <TextField size="small" label="Target" value={simForm.target} onChange={(e) => setSimForm((f) => ({ ...f, target: e.target.value }))} sx={{ flex: 1 }} />
          </Stack>
          <TextField size="small" multiline minRows={2} label="Additional fields (JSON: method, query_string, filename, sha256 …)" value={simForm.payload} onChange={(e) => setSimForm((f) => ({ ...f, payload: e.target.value }))} />
          <Button variant="contained" size="small" onClick={handleSimulate} disabled={simulating} sx={{ alignSelf: 'flex-start' }}>{simulating ? 'Sending…' : 'Simulate Event'}</Button>
          {lastSimulation && (
            <Alert severity={lastSimulation.threat_detected ? 'warning' : 'info'}>
              Event recorded. {lastSimulation.threat_detected
                ? `${lastSimulation.alerts.length} alert(s) raised: ${lastSimulation.alerts.map((a) => a.summary).join('; ')}`
                : 'No published rule matched — publish a matching rule first to raise an alert.'}
            </Alert>
          )}
        </Stack>
      </Paper>

      {ruleDialogOpen && (
        <NewRuleDialog
          onClose={() => setRuleDialogOpen(false)}
          onSubmit={async (payload) => {
            const ok = await runAction(addRule, payload);
            if (ok) setRuleDialogOpen(false);
            return ok;
          }}
        />
      )}

      <Dialog open={Boolean(testTarget)} onClose={() => { setTestTarget(null); dispatch(clearTestResult()); }} maxWidth="xs" fullWidth>
        <DialogTitle>Test Rule — {testTarget?.name}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth multiline minRows={5} sx={{ mt: 1, fontFamily: 'monospace' }}
            label="Sample event (JSON)" value={testEventJson} onChange={(e) => setTestEventJson(e.target.value)}
          />
          {lastTestResult && (
            <Alert severity={lastTestResult.matched ? 'warning' : 'success'} sx={{ mt: 2 }}>
              {lastTestResult.matched ? 'Matched' : 'Did not match'} (rule v{lastTestResult.rule_version})
              {lastTestResult.expected_match !== null && (lastTestResult.passed ? ' — as expected' : ' — unexpected result')}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => { setTestTarget(null); dispatch(clearTestResult()); }}>Close</Button>
          <Button variant="contained" onClick={handleTestRule}>Run Test</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={suppressionDialogOpen} onClose={() => setSuppressionDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add Suppression</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField select label="Scope to Rule (optional)" size="small" value={suppressionForm.rule_id} onChange={(e) => setSuppressionForm((f) => ({ ...f, rule_id: e.target.value }))}>
              <MenuItem value="">All rules</MenuItem>
              {rules.map((r) => <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>)}
            </TextField>
            <TextField select label="Field" size="small" value={suppressionForm.field} onChange={(e) => setSuppressionForm((f) => ({ ...f, field: e.target.value }))}>
              {SUPPRESSION_FIELDS.map((f) => <MenuItem key={f} value={f}>{f}</MenuItem>)}
            </TextField>
            <TextField label="Value" size="small" value={suppressionForm.value} onChange={(e) => setSuppressionForm((f) => ({ ...f, value: e.target.value }))} autoFocus />
            <TextField label="Reason" size="small" multiline minRows={2} value={suppressionForm.reason} onChange={(e) => setSuppressionForm((f) => ({ ...f, reason: e.target.value }))} />
            <TextField label="Expires At" type="datetime-local" size="small" InputLabelProps={{ shrink: true }} value={suppressionForm.expires_at}
              onChange={(e) => setSuppressionForm((f) => ({ ...f, expires_at: e.target.value }))} helperText="Must be within the next 90 days." />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setSuppressionDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateSuppression}>Add Suppression</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
